import React, { useContext, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Add, Home } from '@mui/icons-material';
import { Box, Breadcrumbs, Fab, styled } from '@mui/material';
import { generatePath, Link, matchPath, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import DonationRequestsListView from '../../Donation/DonationManage/DonationRequestsList/DonationRequestsListView';
import {
  StyledRescuerBreadcrumb,
  StyledRescuerTextBreadcrumb,
} from '../../rescuerDashboard/rescuerDashboardMobile/Components/StyledRescuerBreadcrumb';
import routes from '../../../routes';
import FoodDonorMobileRequestEdit from './Components/FoodDonorMobileRequestEdit';
// eslint-disable-next-line max-len
import DonationRequestCreateMobile from '../../Donation/DonationManage/DonationRequestCreate/DonationRequestCreateMobile';

const FABLabel = styled('span')(({ expanded }) => ({
  maxWidth: expanded ? '180px' : '0px',
  opacity: expanded ? 1 : 0,
  overflow: 'hidden',
  transition: 'max-width 250ms cubic-bezier(0.4, 0, 0.2, 1) 50ms, opacity 200ms ease 100ms',
  marginLeft: expanded ? '8px' : '0px',
  display: 'inline-block',
}));

const FoodDonorMobileDonateFoodBreadcrumbs = ({ showEditBreadcrumb, showCreateBreadcrumb }) => {
  const { formatMessage } = useIntl();

  return (
    <Box boxShadow={0}>
      <Breadcrumbs aria-label="breadcrumb">
        <StyledRescuerBreadcrumb
          component={Link}
          to={generatePath(routes.mobileDashboard)}
          label={formatMessage({ id: 'breadcrumbs.home' })}
          icon={<Home style={{ color: '#fff' }} fontSize="small" />}
        />
        <StyledRescuerTextBreadcrumb
          component={Link}
          to={generatePath(routes.mobileDashboardFoodDonorRequests)}
          label={formatMessage({ id: 'breadcrumbs.food-donor-donate-food' })}
        />
        {showEditBreadcrumb && (
          <StyledRescuerTextBreadcrumb
            component="a"
            href="#"
            label={formatMessage({ id: 'breadcrumbs.food-donor-edit-request' })}
          />
        )}
        {showCreateBreadcrumb && (
          <StyledRescuerTextBreadcrumb
            component="a"
            href="#"
            label={formatMessage({ id: 'breadcrumbs.food-donor-add-request' })}
          />
        )}
      </Breadcrumbs>
    </Box>
  );
};

const paths = [
  generatePath(routes.mobileDashboardFoodDonorRequests),
  routes.mobileDashboardFoodDonorRequestEdit,
  generatePath(routes.mobileDashboardFoodDonorRequestAdd),
];

const FoodDonorMobileRequestsContainer = () => {
  const history = useHistory();
  const scrollTop = () => window.scrollTo({ left: 0, top: 0, behavior: 'smooth' });
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  const fabRef = useRef(null);

  const showCreateRequest = matchPath(location.pathname, {
    path: paths[0],
    exact: true,
  });
  const isEditRequest = matchPath(location.pathname, {
    path: routes.mobileDashboardFoodDonorRequestEdit,
  });
  const isCreateRequest = matchPath(location.pathname, {
    path: routes.mobileDashboardFoodDonorRequestAdd,
    exact: true,
  });

  const handleCreateRequestClick = () => {
    if (expanded) {
      history.push(paths[2]);
    } else {
      setExpanded(true);
    }
  };

  useEffect(() => {
    scrollTop();
  }, []);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!fabRef.current?.contains(e.target)) {
        setExpanded(false);
      }
    };

    if (expanded) {
      document.addEventListener('touchstart', handleOutsideClick, { passive: true });
    }

    return () => {
      document.addEventListener('touchstart', handleOutsideClick);
    };
  }, [expanded]);

  return (
    <>
      <FoodDonorMobileDonateFoodBreadcrumbs showEditBreadcrumb={isEditRequest} showCreateBreadcrumb={isCreateRequest} />

      <Box py={2}>
        <Switch>
          <Route path={paths[0]} exact render={() => <DonationRequestsListView isMobileView history={history} />} />
          <Route path={paths[1]} component={FoodDonorMobileRequestEdit} />
          <Route
            path={paths[2]}
            exact
            render={() => <DonationRequestCreateMobile isMobileView history={history} location={location} />}
          />
        </Switch>

        {showCreateRequest && (
          <Fab
            color="secondary"
            ref={fabRef}
            aria-label="add"
            sx={{
              position: 'absolute',
              bottom: Capacitor.isNativePlatform() ? 'calc(32px + var(--safe-area-inset-bottom, 0px))' : 16,
              right: 16,
              transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1), border-radius 300ms cubic-bezier(0.4, 0, 0.2, 1)',
              width: expanded ? '180px' : '56px',
              borderRadius: expanded ? '28px' : '50%',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
            onClick={handleCreateRequestClick}
          >
            <Add />
            <FABLabel expanded={expanded ? 1 : 0}>Create Request</FABLabel>
          </Fab>
        )}
      </Box>
    </>
  );
};

export default FoodDonorMobileRequestsContainer;
